import React, { useCallback, useRef } from 'react';
import cn from 'classnames';
import moment from 'moment';

import ErrorBoundary from 'components/core/ErrorBoundary.component';
import DatePickerInput from 'components/form/DatePickerInput.component';
import Icon from 'components/generic/Icon.component';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToIso, fromMomentToIso, fromMomentToZulu } from 'utils/converters/time/from.util';
import { formatDayToYearFullWeekday, formatDayToYearShortWeekday } from 'utils/date/format.util';
import { nowAsTimestamp } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import asObject from 'utils/objects/asObject.util';
import parseDate from 'utils/parsers/parseDate';
import isMissing from 'utils/predicates/any/isMissing.util';

import CN from './CalendarInput.module.scss';

const CTAG = 'CalendarInput';

// NOTE: keep moment local to this file and this component, don't leak it to the outside

const asDatePickerInput = (value) => {
  // the 3rd party DatePicker component seems to rely on `.clone()` from `moment` and throws error otherwise
  // and also has issues if the date is invalid
  // but passing null works for the times when there isn't a value to pass

  if (!value) return null;

  const v = moment(value);
  return v.isValid() ? v : null;
};

// use ISO formatted strings to pass dates in and out since it provides flexibility with things like time zone
const CalendarInput = ({
  align,
  disabled,
  minDate: minDateAsIso,
  name: nameFromProps,
  onChange = alwaysNoop,
  shortTime,
  tag: tagFromProps,
  value: valueFromProps,
  width,
}) => {
  const tag = fromAnyToTrimmed(tagFromProps);
  const name = fromAnyToTrimmed(nameFromProps);

  const { isDarkMode } = useTheme();

  const [localIso, setLocalIso] = useResettingState(
    valueFromProps ? fromAnyDateToIso(valueFromProps) : null,
    useResettingState.DIFFERENT,
  );
  const localNum = parseDate(localIso);

  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isPickerRendered, tip: togglePicker, nay: closePicker } = useTip(false);
  const handleToggle = useCallback(() => void (!disabled && togglePicker()), [disabled, togglePicker]);

  const componentRef = useRef(null);
  useOnClickOutside(componentRef, closePicker);

  const handleChange = useCallback(
    (valueAsMoment) => {
      const iso = fromMomentToIso(valueAsMoment);
      const zulu = fromMomentToZulu(iso);
      const num = parseDate(iso);

      const ev = { raw: iso, iso, zulu, num, tag, name, dirty: num !== localNum };
      const { value, dirty } = asObject(call(`${CTAG}.${name}`, onChange, ev));

      const newIso = isMissing(value) ? iso : value;

      setLocalIso(newIso);
      setDirty(isMissing(dirty) ? parseDate(newIso) !== parseDate(valueFromProps) : dirty);
      closePicker();
    },
    [closePicker, localNum, name, onChange, setDirty, setLocalIso, tag, valueFromProps],
  );

  const format = shortTime ? formatDayToYearShortWeekday : formatDayToYearFullWeekday;
  const displayDate = format(localNum || nowAsTimestamp());

  return (
    <div
      data-bem="CalendarInput"
      className={cn({
        [CN.component]: true,
        [CN[`${align}Align`]]: align,
        [CN[`${width}Width`]]: width,
        [CN.darkMode]: isDarkMode,
      })}
      ref={componentRef}
      data-tag={tag}
      data-field-name={name}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
    >
      <button
        className={cn({
          [CN.toggler]: true,
          [CN.active]: isPickerRendered,
        })}
        onClick={handleToggle}
      >
        <div className={CN.inputContainer}>
          <Icon variant="calendar" variantInvert />
          <div className={CN.dateContainer}>{displayDate}</div>
        </div>
      </button>

      {isPickerRendered && (
        <ErrorBoundary tag="CalendarInput__error">
          <DatePickerInput
            tag="CalendarInput__value"
            component={DatePickerInput.ReactDatePickerOld}
            calendarClassName={CN.picker}
            selected={asDatePickerInput(localIso)}
            minDate={asDatePickerInput(minDateAsIso)}
            locale="en-GB"
            calendarStartDay={1} // Weeks start on Monday
            dateFormat="dd, D MMM YYYY"
            onChange={handleChange}
            shouldCloseOnSelect // if false -> Don't hide calendar on date selection
            inline
            dropdownMode="scroll"
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

export default CalendarInput;
